import { toast } from 'sonner';

const SHOPIFY_API_VERSION = '2025-07';
const SHOPIFY_STORE_PERMANENT_DOMAIN = 'jyqig1-r9.myshopify.com';
const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
const SHOPIFY_STOREFRONT_TOKEN = '547b8f366e1a39506649a69d9fe21f9b';

/* ── Hidden vendors ──
   Products from these vendors are filtered out of every listing/detail call.
   They remain in Shopify (not deleted) — just hidden from the storefront.
*/
const HIDDEN_VENDORS = new Set<string>([
  'euro-gents-shop-ym8e1', // Trendsi dropship — not active supplier yet
  'vaara',                  // Unidentified single product
]);

function isHiddenVendor(vendor: string | undefined | null): boolean {
  return HIDDEN_VENDORS.has((vendor ?? '').trim().toLowerCase());
}

/* ── Types ── */

export interface ShopifyImage {
  url: string;
  altText: string | null;
}

export interface ShopifyMediaImage {
  __typename: 'MediaImage';
  alt: string | null;
  image: { url: string; altText: string | null };
}

export interface ShopifyVideo {
  __typename: 'Video';
  alt: string | null;
  sources: { url: string; mimeType: string }[];
}

export interface ShopifyExternalVideo {
  __typename: 'ExternalVideo';
  alt: string | null;
  embedUrl: string;
  host: string;
}

export interface ShopifyModel3d {
  __typename: 'Model3d';
  alt: string | null;
  sources: { url: string; mimeType: string }[];
}

export type ShopifyMedia = ShopifyMediaImage | ShopifyVideo | ShopifyExternalVideo | ShopifyModel3d;

export interface ShopifyMetafield {
  namespace: string;
  key: string;
  value: string;
  type: string;
}

export interface ShopifyVariant {
  id: string;
  title: string;
  price: { amount: string; currencyCode: string };
  compareAtPrice: { amount: string; currencyCode: string } | null;
  availableForSale: boolean;
  quantityAvailable: number | null;
  selectedOptions: { name: string; value: string }[];
  sku: string | null;
  barcode: string | null;
  weight: number | null;
  weightUnit: string | null;
  image: { url: string; altText: string | null } | null;
}

export interface ShopifyProduct {
  id: string;
  title: string;
  description: string;
  descriptionHtml: string;
  handle: string;
  vendor: string;
  productType: string;
  tags: string[];
  seo: { title: string | null; description: string | null };
  priceRange: {
    minVariantPrice: { amount: string; currencyCode: string };
  };
  images: { edges: { node: ShopifyImage }[] };
  media: { edges: { node: ShopifyMedia }[] };
  variants: { edges: { node: ShopifyVariant }[] };
  options: { name: string; values: string[] }[];
  metafields: (ShopifyMetafield | null)[];
}

/* ── API helper ── */

async function storefrontApiRequest(query: string, variables: Record<string, unknown> = {}) {
  const response = await fetch(SHOPIFY_STOREFRONT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (response.status === 402) {
    toast.error('Shopify: Payment required', {
      description: 'Your store needs an active Shopify billing plan. Visit https://admin.shopify.com to upgrade.',
    });
    return null;
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  if (data.errors?.length) {
    const fatalErrors = data.errors.filter(
      (e: { extensions?: { code?: string; requiredAccess?: string } }) =>
        !(e.extensions?.code === 'ACCESS_DENIED' && e.extensions?.requiredAccess?.includes('unauthenticated_read_product_inventory'))
    );

    if (fatalErrors.length > 0 || !data.data) {
      throw new Error(`Shopify error: ${data.errors.map((e: { message: string }) => e.message).join(', ')}`);
    }
  }
  return data;
}

/* ── Shared product fragments ──
   CardProductFields → slim (lists/grids). ~70% smaller payload.
   ProductFields     → rich (product detail page only).
*/

const CARD_PRODUCT_FIELDS = `
  fragment CardProductFields on Product {
    id
    title
    handle
    vendor
    productType
    tags
    priceRange {
      minVariantPrice {
        amount
        currencyCode
      }
    }
    images(first: 2) {
      edges {
        node {
          url
          altText
        }
      }
    }
    variants(first: 20) {
      edges {
        node {
          id
          title
          price {
            amount
            currencyCode
          }
          compareAtPrice {
            amount
            currencyCode
          }
          availableForSale
          quantityAvailable
          selectedOptions {
            name
            value
          }
        }
      }
    }
    options {
      name
      values
    }
  }
`;

const PRODUCT_FIELDS = `
  fragment ProductFields on Product {
    id
    title
    description
    descriptionHtml
    handle
    vendor
    productType
    tags
    seo {
      title
      description
    }
    priceRange {
      minVariantPrice {
        amount
        currencyCode
      }
    }
    images(first: 10) {
      edges {
        node {
          url
          altText
        }
      }
    }
    media(first: 10) {
      edges {
        node {
          ... on MediaImage {
            __typename
            alt
            image {
              url
              altText
            }
          }
          ... on Video {
            __typename
            alt
            sources {
              url
              mimeType
            }
          }
          ... on ExternalVideo {
            __typename
            alt
            embedUrl
            host
          }
          ... on Model3d {
            __typename
            alt
            sources {
              url
              mimeType
            }
          }
        }
      }
    }
    variants(first: 30) {
      edges {
        node {
          id
          title
          price {
            amount
            currencyCode
          }
          compareAtPrice {
            amount
            currencyCode
          }
          availableForSale
          quantityAvailable
          selectedOptions {
            name
            value
          }
          sku
          barcode
          weight
          weightUnit
          image {
            url
            altText
          }
        }
      }
    }
    options {
      name
      values
    }
    metafields(identifiers: []) {
      namespace
      key
      value
      type
    }
  }
`;

/* ── Queries ── */

const PRODUCTS_QUERY = `
  ${PRODUCT_FIELDS}
  query GetProducts($first: Int!, $query: String, $country: CountryCode!) @inContext(country: $country) {
    products(first: $first, query: $query, sortKey: CREATED_AT, reverse: true) {
      edges {
        node {
          ...ProductFields
        }
      }
    }
  }
`;

const PRODUCTS_PAGE_QUERY = `
  ${PRODUCT_FIELDS}
  query GetProductsPage($first: Int!, $query: String, $after: String, $country: CountryCode!) @inContext(country: $country) {
    products(first: $first, query: $query, sortKey: CREATED_AT, reverse: true, after: $after) {
      edges {
        cursor
        node {
          ...ProductFields
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const CARDS_PAGE_QUERY = `
  ${CARD_PRODUCT_FIELDS}
  query GetCardsPage($first: Int!, $query: String, $after: String, $country: CountryCode!) @inContext(country: $country) {
    products(first: $first, query: $query, sortKey: CREATED_AT, reverse: true, after: $after) {
      edges {
        cursor
        node {
          ...CardProductFields
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const PRODUCT_BY_HANDLE_QUERY = `
  ${PRODUCT_FIELDS}
  query GetProductByHandle($handle: String!, $country: CountryCode!) @inContext(country: $country) {
    productByHandle(handle: $handle) {
      ...ProductFields
    }
  }
`;

const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
      }
      userErrors {
        field
        message
      }
    }
  }
`;

/* ── Public API ── */

/* ── Country / currency context ──
   The Storefront API's @inContext directive returns prices in the local
   currency of the requested country, IF the merchant has enabled that
   country/market in Shopify Markets. We default to US so prices come back
   in USD (matching the prices the merchant intends to sell at in the US
   market). For Hebrew UI we request IL → ILS.
*/
function countryForLang(lang: string): string {
  const l = (lang || 'en').toLowerCase().split('-')[0];
  if (l === 'he') return 'IL';
  return 'US';
}

let currentLang = 'en';
export function setShopifyLang(lang: string) {
  currentLang = lang;
}

// Always require minimum stock — products with <5 inventory are hidden site-wide.
// Also always exclude Gift Card products from regular shop listings — they have
// a dedicated /gift-card page and should never appear as a normal product card.
const MIN_STOCK_FILTER = 'inventory_total:>=5';
const EXCLUDE_GIFT_CARD = "NOT product_type:'Gift Card'";
const BASE_FILTER = `${MIN_STOCK_FILTER} AND ${EXCLUDE_GIFT_CARD}`;
function withStockFilter(query?: string): string {
  return query && query.trim().length > 0
    ? `(${query}) AND ${BASE_FILTER}`
    : BASE_FILTER;
}

export async function fetchProducts(
  first: number | null = 50,
  query?: string,
): Promise<ShopifyProduct[]> {
  if (first === null) {
    return fetchAllProducts(query);
  }
  const data = await storefrontApiRequest(PRODUCTS_QUERY, {
    first,
    query: withStockFilter(query),
    country: countryForLang(currentLang),
  });
  if (!data) return [];
  return data.data.products.edges
    .map((edge: { node: ShopifyProduct }) => edge.node)
    .filter((p: ShopifyProduct) => !isHiddenVendor(p.vendor));
}

export async function fetchAllProducts(query?: string): Promise<ShopifyProduct[]> {
  const PAGE_SIZE = 250;
  const all: ShopifyProduct[] = [];
  let after: string | null = null;
  for (let i = 0; i < 100; i++) {
    const data = await storefrontApiRequest(PRODUCTS_PAGE_QUERY, {
      first: PAGE_SIZE,
      query: withStockFilter(query),
      after,
      country: countryForLang(currentLang),
    });
    if (!data) break;
    const page = data.data.products;
    for (const edge of page.edges) {
      if (!isHiddenVendor(edge.node.vendor)) all.push(edge.node);
    }
    if (!page.pageInfo.hasNextPage) break;
    after = page.pageInfo.endCursor;
  }
  return all;
}

export interface ProductsPage {
  products: ShopifyProduct[];
  endCursor: string | null;
  hasNextPage: boolean;
}

export async function fetchProductsPage(
  first: number,
  query?: string,
  after: string | null = null,
): Promise<ProductsPage> {
  const data = await storefrontApiRequest(PRODUCTS_PAGE_QUERY, {
    first,
    query: withStockFilter(query),
    after,
    country: countryForLang(currentLang),
  });
  if (!data) return { products: [], endCursor: null, hasNextPage: false };
  const page = data.data.products;
  return {
    products: page.edges
      .map((edge: { node: ShopifyProduct }) => edge.node)
      .filter((p: ShopifyProduct) => !isHiddenVendor(p.vendor)),
    endCursor: page.pageInfo.endCursor,
    hasNextPage: page.pageInfo.hasNextPage,
  };
}

/* ── Slim card type (subset of ShopifyProduct fields actually used by grids) ──
   We return ShopifyProduct-shaped objects with empty defaults for the heavy
   fields so existing consumers (ShopifyProductCard, filterForCollection)
   remain type-compatible without changes.
*/
export interface CardsPage {
  products: ShopifyProduct[];
  endCursor: string | null;
  hasNextPage: boolean;
}

function inflateCardProduct(node: Partial<ShopifyProduct> & { id: string; handle: string; title: string }): ShopifyProduct {
  return {
    description: '',
    descriptionHtml: '',
    seo: { title: null, description: null },
    media: { edges: [] },
    options: [],
    metafields: [],
    ...node,
    variants: node.variants ?? { edges: [] },
    images: node.images ?? { edges: [] },
    tags: node.tags ?? [],
    vendor: node.vendor ?? '',
    productType: node.productType ?? '',
    priceRange: node.priceRange ?? { minVariantPrice: { amount: '0', currencyCode: 'USD' } },
  } as ShopifyProduct;
}

export async function fetchCardsPage(
  first: number,
  query?: string,
  after: string | null = null,
): Promise<CardsPage> {
  const data = await storefrontApiRequest(CARDS_PAGE_QUERY, {
    first,
    query: withStockFilter(query),
    after,
    country: countryForLang(currentLang),
  });
  if (!data) return { products: [], endCursor: null, hasNextPage: false };
  const page = data.data.products;
  return {
    products: page.edges
      .map((edge: { node: Partial<ShopifyProduct> & { id: string; handle: string; title: string } }) =>
        inflateCardProduct(edge.node),
      )
      .filter((p: ShopifyProduct) => !isHiddenVendor(p.vendor)),
    endCursor: page.pageInfo.endCursor,
    hasNextPage: page.pageInfo.hasNextPage,
  };
}

export async function fetchProductByHandle(handle: string): Promise<ShopifyProduct | null> {
  const data = await storefrontApiRequest(PRODUCT_BY_HANDLE_QUERY, {
    handle,
    country: countryForLang(currentLang),
  });
  if (!data) return null;
  const product = data.data.productByHandle as ShopifyProduct | null;
  if (product && isHiddenVendor(product.vendor)) return null;
  // Gift cards have their own dedicated /gift-card page — never expose them
  // as a normal product page.
  if (product && (product.productType || '').toLowerCase() === 'gift card') return null;
  return product;
}

export interface CartLineInput {
  merchandiseId: string;
  quantity: number;
}

export interface CheckoutBuyerAddress {
  firstName?: string;
  lastName?: string;
  address1: string;
  address2?: string;
  city: string;
  province?: string;
  country: string;
  zip: string;
  phone?: string;
}

export interface CheckoutBuyerIdentity {
  email?: string;
  phone?: string;
  address?: CheckoutBuyerAddress;
}

export async function createShopifyCart(
  lines: CartLineInput[],
  buyer?: CheckoutBuyerIdentity,
): Promise<string | null> {
  const input: Record<string, unknown> = { lines };
  if (buyer && (buyer.email || buyer.address)) {
    const buyerIdentity: Record<string, unknown> = {};
    if (buyer.email) buyerIdentity.email = buyer.email;
    if (buyer.phone) buyerIdentity.phone = buyer.phone;
    if (buyer.address) {
      buyerIdentity.deliveryAddressPreferences = [
        {
          deliveryAddress: {
            firstName: buyer.address.firstName,
            lastName: buyer.address.lastName,
            address1: buyer.address.address1,
            address2: buyer.address.address2,
            city: buyer.address.city,
            province: buyer.address.province,
            country: buyer.address.country,
            zip: buyer.address.zip,
            phone: buyer.address.phone,
          },
        },
      ];
    }
    input.buyerIdentity = buyerIdentity;
  }
  const data = await storefrontApiRequest(CART_CREATE_MUTATION, { input });
  if (!data) return null;
  const { cart, userErrors } = data.data.cartCreate;
  if (userErrors?.length > 0) {
    throw new Error(userErrors.map((e: { message: string }) => e.message).join(', '));
  }
  const checkoutUrl = new URL(cart.checkoutUrl);
  checkoutUrl.searchParams.set('channel', 'online_store');
  return checkoutUrl.toString();
}

/* ── Helpers ── */

/**
 * Add Shopify CDN transformation params to resize/reformat images on the fly.
 */
export function shopifyImage(url: string, width = 800): string {
  if (!url || !url.includes('cdn.shopify.com')) return url;
  try {
    const u = new URL(url);
    u.searchParams.set('width', String(width));
    // Hint Shopify to optimize compression for our use case.
    if (!u.searchParams.has('format')) u.searchParams.set('format', 'webp');
    return u.toString();
  } catch {
    return url;
  }
}

export function getProductImages(product: ShopifyProduct, width = 800): string[] {
  return product.images.edges.map(e => shopifyImage(e.node.url, width));
}

export function getProductVariants(product: ShopifyProduct): ShopifyVariant[] {
  return product.variants.edges.map(e => e.node);
}

export function getProductMedia(product: ShopifyProduct): ShopifyMedia[] {
  return product.media.edges.map(e => e.node);
}

export function getMinPrice(product: ShopifyProduct): string {
  return parseFloat(product.priceRange.minVariantPrice.amount).toFixed(2);
}

export function getCurrencyCode(product: ShopifyProduct): string {
  return product.priceRange.minVariantPrice.currencyCode || 'USD';
}

/* ── Price formatting ──
   We trust the currencyCode returned by the Storefront API. Because every
   query is made @inContext(country: ...), Shopify returns the price already
   in the correct local currency (USD for US, ILS for IL, etc.) via Shopify
   Markets. We do NOT apply any manual conversion rate.
*/
export function formatPrice(amount: number, currencyCode: string, locale = 'en'): string {
  // Prices use charm pricing (e.g. $32.99) — always show 2 decimals so the
  // .99 ending is preserved. Rounding to whole would hide the strategy.
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode || 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatWeight(weight: number, unit: string): string {
  const unitMap: Record<string, string> = {
    KILOGRAMS: 'kg',
    GRAMS: 'g',
    POUNDS: 'lb',
    OUNCES: 'oz',
  };
  return `${weight} ${unitMap[unit] || unit}`;
}
